import React, { useState, useEffect, useCallback } from 'react';

import { format } from 'date-fns';

import Users from '../../../api/Users';
import SoftCard from '../../../components/common/SoftCard';
import Accordion from '../../../components/common/Accordion';

import styles from './Tokens.module.scss'
import { BulletIcon } from '../../../components/common/BulletIcon/BulletIcon';

/**
 * This token list will take an array of tokens, seperate them by name but include an accordion that shows each token individually  
 *   
 * @param {int} userId - the user that will have their tokens displayed  
 * @param {bool} showExpired - if expired tokens are displayed (overridden by status tabs instead)  
 * @param {bool} showRedeemed - if the redeemed tokens are displayed (overridden by status tabs)  
 * @param {bool} showAvailable - true by default - will also be the active tab, by default, if statusTab is true  
 * @param {bool} statusTabs - if this is true, instead of displaying all the available, expired, redeemed in selectable tabs  
 * @param {string} displayType - If accordion, will be grouped as a singular item per token, expandable for individual tokens  
 * If displayType is cards, each token will be shown as individual cards    
 * If displayType is list, each token will be shown as a list  
 * @param {function} tokenFilter - if anything needs to filter the token result (such as for an event or service), pass through a function that takes in the tokens and returns the tokens  
 * @param {object} cardProps - passed to the soft card as additional props  
 * @param {object} accordionProps - the props passed to the accordion to change it from default  
 *   
 */
export const TokenList=({
    userId=null,
    showExpired=false,
    showRedeemed=false,
    showAvailable=true,
    statusTabs=false,
    displayType="accordion",
    tokenFilter=null,
    cardProps={maxWidth: "250px"},
    accordionProps={primary: false},
    tokenClick=null
})=>{

    const [loading, setLoading]=useState(true)
    const [tokens, setTokens]=useState([]);
    const [activeTab, setActiveTab]=useState("available") //available, redeemed, expired

    //we want to sort by tokens for 
    const sortTokens=useCallback((tokens)=>{
        const groupedTokens = tokens.reduce((acc, token)=>{
            const key= token?.product_name;

            if(!acc[key]){
                acc[key]={
                    id: token.product_id,
                    product_name: token?.product_name,
                    number_of_tokens: 0,
                    exp_dates:[], //this is to sort them to find the date of expiring most recently to provide that info on condensed views
                    product_variant_id: token.product_variant_id,
                    tokens:[]
                }
            }
            acc[key].number_of_tokens++;
            if(token.exp_date){
                acc[key].exp_dates.push(token.exp_date)
            }
            
            acc[key].tokens.push(token) //so we have a whole list of the tokens after sorting for the larger display
            if(activeTab === "available") token.sentence = token?.exp_date ? `Expires on ${format(new Date(token.exp_date), "MM/dd/yyy")}` : "No Expiration";
            if(activeTab === "expired") token.sentence = token?.exp_date ? `Expired on ${format(new Date(token.exp_date), "MM/dd/yyyy")}` : "Expiration Date Unkown"
            if(activeTab === "redeemed") token.sentence = token?.use_date ? `Redeemed on ${format(new Date(token.use_date), "MM/dd/yyyy")}` : "Use date unknown"
            return acc;
        },{})

        Object.values(groupedTokens).forEach(group=>{
            if(group.exp_dates.length > 0){
                group.soonest_exp = group.exp_dates.reduce((earliest, current) => {
                    const currentDate = new Date(current);
                    const earliestDate = new Date(earliest);
                    return currentDate < earliestDate ? current : earliest;
                });
            }else group.soonest_exp = null
            if(displayType === "accordion") {
                group.title=`${group?.product_name} (${group?.number_of_tokens} tokens total) ` 
                if(activeTab==="available") group.title += `: 
                    ${group?.soonest_exp ? 
                        `Soonest expiring token ${format(new Date(group.soonest_exp), "MM/dd/yyyy")}` 
                        : 
                        `None of these tokens expire`
                    }`
                group.content = <ul>
                    {group.tokens?.map(token=>(
                        <li key={`each-token-${token?.id}`}>
                            {token?.sentence}
                        </li>
                    ))}
                </ul>
            }
        })

        return Object.values(groupedTokens)
    },[activeTab, displayType])

    //according to the docs for the params, because it seems like an antipattern
    //include_expired:  0-unexpired     1-expired & unexpired    2-expired
    //include_redeemed: 0-not redeemed  1-redeemed and not       2-redeemed
    const getTokens=useCallback(async()=>{
        let params = {
            user_id: userId
        }
        if(activeTab==="available"){
            params.include_expired = 0;
            params.include_redeemed = 0;
        }else if(activeTab==="redeemed"){
            params.include_expired = 1;
            params.include_redeemed = 2;
        }else if (activeTab==="expired"){
            params.include_expired= 2;
            params.included_redeemed= 0;
        }

        try{
            let response = await Users.tokens(params);
            if(response.status === 200 && response.data){
                let tokens = response.data
                if(tokenFilter) tokens = tokenFilter(response.data);
                tokens = await sortTokens(tokens);
                setTokens(tokens);
            }
            //condense the tokens into the same one
            //display the same ones when expanded per each one
        }catch(ex){
            console.error(ex)
        }
        setLoading(false)
    },[userId, sortTokens, tokenFilter, activeTab]);

    useEffect(()=>{
        getTokens();
    },[activeTab, getTokens])

    useEffect(()=>{
        if(!statusTabs && showExpired) setActiveTab("expired");
        else if(!statusTabs && showRedeemed) setActiveTab("redeemed");
        else if(!statusTabs && showAvailable) setActiveTab("available")
    },[showAvailable, showRedeemed, showExpired, statusTabs])

    const handleTabChange=(tab)=>{
        if(tab !== activeTab){
            setLoading(true)
            setTokens([])
            setActiveTab(tab)
        }
    }

    if(loading) return(
        <span>
            Loading...
        </span>
    )

    return(
        <div className={styles["token-list"]}>
            {tokens?.length < 1 ?
                <p>
                    No tokens to display
                </p>
                :
                <>
                    {statusTabs &&
                        <>
                            <div className={styles["status-div"]}>
                                <span className={`
                                        ${styles["status-tab"]} 
                                        ${activeTab==="available" ? styles["active"] : ""}
                                    `} 
                                    onClick={()=>handleTabChange("available")}
                                >
                                    Available
                                </span>
                                <span className={`
                                        ${styles["status-tab"]} 
                                        ${activeTab==="redeemed" ? styles["active"] : ""}
                                    `} 
                                    onClick={()=>handleTabChange("redeemed")}
                                >
                                    Redeemed
                                </span>
                                <span className={`
                                        ${styles["status-tab"]} 
                                        ${activeTab==="expired" ? styles["active"] : ""}
                                    `} 
                                    onClick={()=>handleTabChange("expired")}
                                >
                                    Expired
                                </span>
                            </div>
                        </>
                    }
                    {displayType === "accordion" && tokens?.length &&
                        <Accordion 
                            items={tokens}
                            {...accordionProps}
                        />
                    }
                    {
                    // (displayType === "cards" || displayType === "list") && 
                    tokens?.map((tokenGroup, i)=>(
                        <React.Fragment key={`token-group-${i}`}>
                            {displayType === "cards" &&
                                <>
                                    {tokenGroup?.tokens?.map((token,i)=>(
                                        <SoftCard
                                            key={`token-card-${i}`}
                                            {...cardProps}
                                            onClick={tokenClick ? ()=>tokenClick(token) : null}
                                        >
                                            <strong>
                                                {token?.product_name}
                                            </strong>
                                            <br />
                                            <BulletIcon bulletType={"openCircle"}/> 
                                            {token?.sentence}
                                        </SoftCard>
                                    ))}
                                </>
                            }
                            {displayType === "list" &&
                                <>
                                    <strong>
                                        <u>
                                            {tokenGroup?.product_name} ({tokenGroup?.number_of_tokens} tokens)
                                        </u>
                                    </strong>
                                    <br />
                                    <ul>
                                        {tokenGroup?.tokens?.map((token, i)=>(
                                            <li key={`token-list-item-i`} onClick={tokenClick ? ()=>tokenClick(token) : null}>
                                                {token?.sentence}
                                            </li>
                                        ))}
                                    </ul>
                                    <hr />
                                </>
                            }
                        </React.Fragment>
                    ))}
                </>
            }
        </div>
    )
}